import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "react-final-form";
import { Api } from "../../api/Api";
import { canMaintainReceipts } from "../../store/actions/LoginActions";
import { ReceiptDto } from "../../store/models/receipt/ReceiptDto";
import MenuLayout from "../app/MenuLayout";
import { UrlPathReceiptScanning } from "../../api/url";
import { FormInput } from "../atoms/FormInput";
import { formatEuro, formatUtcToLocalDateTimeInGermanFormat, nameof, ValidationErrors } from "../atoms/Utils";
import { ReceiptItemDto } from "../../store/models/receipt/ReceiptItemDto";
import { DataGrid, GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { IStore } from "../../store/IStore";
import { thunkCreateErrorNotification, thunkCreateSuccessNotification } from "../../store/actions/NotificationActions";
import ErrorPage from "../common/ErrorPage";
import { FormSelect } from "../atoms/FormSelect";
import { ReceiptApprovalStatus } from "../../store/models/receipt/ReceiptApprovalStatus";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { getOperationalUnitOptions } from "../operationalUnit/OperationalUnitForm";
import { FormDateTime } from "../atoms/FormDateTime";
import CustomerNameCell from "../customerCare/CustomerNameCell";
import { isEqual } from "lodash";

const ReceiptEdit = (props: ThunkProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [receipt, setReceipt] = useState<ReceiptDto>({
    receiptNumber: "",
    externalReceiptId: "",
    customerNumber: "",
    totalAmount: "",
    submissionDate: "",
    status: "",
    thumbnailUrl: "",
    imageUrl: "",
    reviewedBy: "",
    rejectedAt: "",
    receiptDate: "",
    unitNumber: "",
    bonusPoints: "",
    items: [],
  });
  // Track validation errors for DataGrid rows
  const [itemValidationErrors, setItemValidationErrors] = useState<{ [key: string]: string }>({});
  // Track original receipt to detect changes
  const [originalReceipt, setOriginalReceipt] = useState<ReceiptDto | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // State for confirmation dialogs
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<"approve" | "reject" | null>(null);
  // State to track current form values for change detection
  // Track the latest form values
  const [currentFormValues, setCurrentFormValues] = useState<any>(null);

  const formApiRef = useRef<any>(null);
  const navigate = useNavigate();
  const isReadonly = !canMaintainReceipts();
  const { operationalUnits } = props;
  const operationalUnitOptions = getOperationalUnitOptions(undefined, operationalUnits, theme, false, false);

  const shouldShowActionButtons = (status: string) => {
    return status === "WAITING_FOR_APPROVAL";
  };

  const isFinalState = (status: string) => {
    return ["COMPLETED", "REJECTED", "APPROVED"].includes(status);
  };

  // Function to check if there are unsaved changes
  const checkUnsavedChanges = useCallback(
    (currentValues: ReceiptDto & Record<string, any>): boolean => {
      // Compare current form values with original receipt
      const formValuesForComparison: Partial<ReceiptDto> = {
        receiptNumber: currentValues.receiptNumber,
        externalReceiptId: currentValues.externalReceiptId,
        customerNumber: currentValues.customerNumber,
        totalAmount: currentValues.totalAmount,
        submissionDate: currentValues.submissionDate,
        status: currentValues.status,
        thumbnailUrl: currentValues.thumbnailUrl,
        imageUrl: currentValues.imageUrl,
        reviewedBy: currentValues.reviewedBy,
        rejectedAt: currentValues.rejectedAt,
        receiptDate: currentValues.receiptDate,
        unitNumber: currentValues.unitNumber,
        bonusPoints: currentValues.bonusPoints,
        items: receipt.items,
      };

      return !isEqual(formValuesForComparison, originalReceipt);
    },
    [receipt.items, originalReceipt]
  );

  // Effect to check for unsaved changes whenever form values or receipt items change
  useEffect(() => {
    if (originalReceipt && currentFormValues) {
      const hasChanges = checkUnsavedChanges(currentFormValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [currentFormValues, receipt.items, originalReceipt, checkUnsavedChanges]);

  useEffect(() => {
    if (id) {
      // Reset unsaved changes state when loading new data
      setHasUnsavedChanges(false);
      setPendingAction(null);
      setShowSaveConfirmDialog(false);

      Api.getReceipt(id)
        .then((data: ReceiptDto) => {
          setReceipt(data);
          // Use deep clone to ensure reference equality doesn't cause issues
          setOriginalReceipt(JSON.parse(JSON.stringify(data)));
        })
        .catch((error) => {
          props.thunkCreateErrorNotification(
            t("receiptScanDialog.errorLoading", "Fehler beim Laden des Belegs!"),
            error
          );
        });
    }
  }, [t, id, props]);

  const updateReceipt = (receiptData: ReceiptDto) => {
    // Validate receipt items before submission
    if (validateAllReceiptItems()) {
      if (receiptData) {
        // Set itemId to null for new items (itemId not present in original list or is empty)
        const itemsForApi = receiptData.items.map((item) => ({
          ...item,
          itemId: item.itemId && item.itemId.toString().startsWith("new-") ? null : item.itemId,
        })) as ReceiptItemDto[];
        let updateData = {
          ...receiptData,
          items: itemsForApi,
        };

        // Return the promise so we can chain actions after save completes
        return Api.updateReceipt(receiptData.receiptNumber, updateData)
          .then(() => {
            // Update original data to match the current state
            const updatedReceipt: ReceiptDto = { ...receiptData, items: itemsForApi };
            setOriginalReceipt(JSON.parse(JSON.stringify(updatedReceipt)));
            setReceipt(updatedReceipt);
            setHasUnsavedChanges(false);
            setCurrentFormValues({ ...receiptData }); // Update form values tracking

            props.thunkCreateSuccessNotification(t("receiptScanDialog.successUpdate"));

            // If we have a pending action, don't navigate away yet
            if (!pendingAction) {
              navigate(UrlPathReceiptScanning);
            }

            // Return the receipt for chaining
            return receiptData;
          })
          .catch((error) => {
            props.thunkCreateErrorNotification(t("receiptScanDialog.errorUpdate"), error);
            throw error; // Re-throw to propagate the error for promise chaining
          });
      }
    } else {
      // Force form revalidation to show the DataGrid errors
      if (formApiRef.current) {
        formApiRef.current.change("_dataGridValidated", false);
        setTimeout(() => formApiRef.current.submit(), 0);
      }
    }
  };

  // Validate all receipt items and update errors state
  const validateAllReceiptItems = () => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {};

    receipt.items.forEach((item, index) => {
      const error = validateReceiptItemRow(item);
      if (error) {
        newErrors[`items[${index}]`] = error;
        isValid = false;
      }
    });

    setItemValidationErrors(newErrors);
    return isValid;
  };

  // This checkUnsavedChanges function has been moved to the top of the component

  // Execute reject with change check
  const handleReject = () => {
    if (hasUnsavedChanges) {
      setPendingAction("reject");
      setShowSaveConfirmDialog(true);
    } else {
      rejectReceipt();
    }
  };

  // Execute approve with change check
  const handleApprove = () => {
    if (hasUnsavedChanges) {
      setPendingAction("approve");
      setShowSaveConfirmDialog(true);
    } else {
      approveReceipt();
    }
  };

  const rejectReceipt = () => {
    if (receipt) {
      Api.rejectReceipt(receipt.receiptNumber)
        .then(() => {
          props.thunkCreateSuccessNotification(t("receiptScanDialog.successReject"));
          navigate(UrlPathReceiptScanning);
        })
        .catch((error) => {
          props.thunkCreateErrorNotification(t("receiptScanDialog.errorReject"), error);
        });
    }
  };

  const approveReceipt = () => {
    if (receipt) {
      Api.approveReceipt(receipt.receiptNumber)
        .then(() => {
          props.thunkCreateSuccessNotification(t("receiptScanDialog.successApprove"));
          navigate(UrlPathReceiptScanning);
        })
        .catch((error) => {
          props.thunkCreateErrorNotification(t("receiptScanDialog.errorApprove"), error);
        });
    }
  };

  const validateForm = (values: ReceiptDto) => {
    const errors: ValidationErrors<ReceiptDto> = {};
    const required = t("messages.validation.required", "This field is required");
    const requiredFields = [
      nameof<ReceiptDto>("externalReceiptId"),
      nameof<ReceiptDto>("totalAmount"),
      nameof<ReceiptDto>("unitNumber"),
    ];
    requiredFields.forEach((field) => {
      if (!values[field]) {
        errors[field] = required;
      }
    });

    // Add DataGrid validation errors to form errors
    Object.keys(itemValidationErrors).forEach((key) => {
      if (itemValidationErrors[key]) {
        (errors as any)[key] = itemValidationErrors[key];
      }
    });

    return errors;
  };

  const handleCancel = () => {
    navigate(UrlPathReceiptScanning);
  };

  if (!receipt) return <ErrorPage />;

  // DataGrid columns for receipt items
  const itemColumns: GridColDef[] = [
    {
      field: "itemName",
      headerName: t("receiptScanDialog.labelItemName"),
      flex: 2,
      minWidth: 120,
      editable: true,
    },
    {
      field: "quantity",
      headerName: t("receiptScanDialog.labelQuantity"),
      flex: 1,
      minWidth: 80,
      editable: true,
    },
    {
      field: "salesPrice",
      headerName: t("receiptScanDialog.labelSalesPrice"),
      flex: 1,
      minWidth: 100,
      editable: true,
      renderCell: (params) => formatEuro(params.value),
    },
    {
      field: "originalPrice",
      headerName: t("receiptScanDialog.labelOriginalPrice"),
      flex: 1,
      minWidth: 100,
      editable: true,
      renderCell: (params) => formatEuro(params.value),
    },
    {
      field: "actions",
      type: "actions",
      width: 80,
      renderCell: (params: any) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label={t("common.delete", "Delete")}
          onClick={() => handleDeleteRow(params.id)}
          color="inherit"
        />,
      ],
    },
  ];

  // Add new row
  const handleAddRow = () => {
    const newRow: ReceiptItemDto = {
      itemId: "new-" + Date.now(), // Unique ID for new items
      itemName: "",
      quantity: "1",
      salesPrice: "0",
      originalPrice: "0",
    };

    setReceipt(prev => ({ ...prev, items: [...prev.items, newRow] }));

    // Update unsaved changes state when adding a row
    setHasUnsavedChanges(true);
  };

  // Remove row
  const handleDeleteRow = (id: string) => {
    // Find the index of the row being deleted
    const rowIndexToDelete = receipt.items.findIndex((item) => item.itemId === id);

    // Update the receipt items by removing the deleted row
    const updatedItems = receipt.items.filter((item) => item.itemId !== id);
    setReceipt(prev => ({ ...prev, items: updatedItems }));

    // Remove validation errors for the deleted row and reindex remaining errors
    const newErrors: { [key: string]: string } = {};

    // Loop through all items to rebuild the error map with updated indices
    receipt.items.forEach((item, currentIndex) => {
      // Skip the deleted row
      if (item.itemId === id) return;

      // Calculate the new index after deletion
      const newIndex = currentIndex > rowIndexToDelete ? currentIndex - 1 : currentIndex;

      // Copy error if it exists for this row (using its current index)
      const errorKey = `items[${currentIndex}]`;
      if (itemValidationErrors[errorKey]) {
        newErrors[`items[${newIndex}]`] = itemValidationErrors[errorKey];
      }
    });

    // Update the validation errors state with reindexed errors
    setItemValidationErrors(newErrors);

    // Update form validation if there's an active form
    if (formApiRef.current) {
      formApiRef.current.change("_dataGridValidated", Object.keys(newErrors).length === 0);
    }

    // Update unsaved changes state when removing a row
    setHasUnsavedChanges(true);
  };

  const receiptTypeOption = Object.values(ReceiptApprovalStatus).map((status) => ({
    label: t(`receiptStatus.${status}`),
    value: status,
  }));

  // Validation for DataGrid row edits
  const validateReceiptItemRow = (row: ReceiptItemDto) => {
    if (!row.itemName || !row.quantity || !row.salesPrice || !row.originalPrice) {
      return t("receiptScanDialog.errorBlankItemField", "All fields are required.");
    }
    return null;
  };

  return (
    <MenuLayout headline={t("receiptScanDialog.headline", "Scanned receipts edit") /* fallback */}>
      <Paper sx={{ p: 3, mt: 4 }}>
        <Grid container spacing={4}>
          {/* Left: Receipt image */}
          <Grid item xs={12} md={5}>
            <Typography variant="body2" mb={4}>
              {t("receiptScanDialog.submittedBy") + " "} <CustomerNameCell customerNumber={receipt.customerNumber} />{" "}
              {t("receiptScanDialog.submittedAt") + " " + formatUtcToLocalDateTimeInGermanFormat(receipt.submissionDate)}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start", height: "100%" }}>
              {receipt.imageUrl && (
                <img
                  src={receipt.imageUrl}
                  alt={t("receiptScanDialog.imageAlt", "Receipt")}
                  style={{ maxWidth: 350, border: "1px solid #ccc", borderRadius: 8 }}
                />
              )}
            </Box>
          </Grid>
          {/* Right: Receipt details in two columns */}
          <Grid item xs={12} md={7}>
            <Form
              onSubmit={(values) => updateReceipt(values)}
              validate={validateForm}
              initialValues={{
                ...receipt,
                _dataGridValidated: true, // Hidden field to track DataGrid validation
              }}
              render={({ handleSubmit, submitting, values, form }) => {
                // Store the form API for external validation
                formApiRef.current = form;

                // Update currentFormValues whenever values change
                // This is safe because it doesn't use hooks, just updates state
                if (JSON.stringify(values) !== JSON.stringify(currentFormValues)) {
                  setCurrentFormValues({ ...values });

                  // Update the form API reference with latest values for external functions
                  if (!formApiRef.current) {
                    formApiRef.current = form;
                  }
                }

                return (
                  <form onSubmit={handleSubmit}>
                    <Typography variant="h6" gutterBottom>
                      {t("receiptScanDialog.details")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormInput
                          type="text"
                          name={nameof<ReceiptDto>("externalReceiptId")}
                          label={t("receiptScanDialog.labelReceiptNumber", "Receipt Number")}
                          readOnly={isReadonly}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormInput
                          type="text"
                          name={nameof<ReceiptDto>("totalAmount")}
                          label={t("receiptScanDialog.labelTotalSum", "Total Sum")}
                          readOnly={isReadonly}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} mb={4}>
                        <FormSelect
                          label={t("bonusRuleSearch.placeHolderOperationalUnit")}
                          name={nameof<ReceiptDto>("unitNumber")}
                          options={operationalUnitOptions}
                          readOnly={isReadonly}
                          fullWidth={true}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} mb={4}>
                        <FormSelect
                          name={nameof<ReceiptDto>("status")}
                          label={t("receiptOverview.receiptSearch.status")}
                          fullWidth
                          readOnly={true}
                          options={receiptTypeOption}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormDateTime
                          readOnly={isReadonly}
                          name={nameof<ReceiptDto>("receiptDate")}
                          label={t("receiptScanDialog.labelPurchaseDate", "Purchase Date")}
                          fullWidth
                        />
                      </Grid>
                    </Grid>

                    {/* Hidden field to trigger form validation for DataGrid errors */}
                    <FormInput type="hidden" name="_dataGridValidated" label="" />

                    {/* Error summary for DataGrid validation */}
                    {Object.keys(itemValidationErrors).length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: "#FFF4F4", borderRadius: 1, border: "1px solid #FFCDD2" }}>
                        <Typography color="error" variant="subtitle2">
                          {t("receiptScanDialog.receiptItemsContainErrors")}
                        </Typography>
                        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                          {Object.keys(itemValidationErrors).map((key, index) => (
                            <li key={index}>
                              <Typography color="error" variant="body2">
                                {t("receiptScanDialog.itemError", "Item {{index}}: {{error}}", {
                                  index: Number(key.match(/\d+/)?.[0] || 0) + 1,
                                  error: itemValidationErrors[key],
                                })}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}

                    {/* DataGrid for receipt items */}
                    <Box sx={{ mt: 6 }}>
                      <Typography variant="h6" gutterBottom>
                        {t("receiptScanDialog.labelReceiptItems", "Receipt Items")}
                      </Typography>

                      <DataGrid
                        autoHeight
                        rows={receipt.items}
                        columns={itemColumns}
                        getRowId={(row) => row.itemId}
                        pageSize={100}
                        hideFooter
                        disableSelectionOnClick
                        sx={{
                          background: "#fff",
                          borderRadius: 2,
                          mt: 2,
                          // Highlight rows with validation errors
                          "& .row-has-error": {
                            backgroundColor: "rgba(255, 0, 0, 0.1)",
                          },
                        }}
                        editMode="row"
                        experimentalFeatures={{ newEditingApi: true }}
                        getRowClassName={(params) => {
                          const rowIndex = receipt.items.findIndex((item) => item.itemId === params.id);
                          return itemValidationErrors[`items[${rowIndex}]`] ? "row-has-error" : "";
                        }}
                        processRowUpdate={(newRow, oldRow) => {
                          // Update receipt items state
                          const updatedItems = receipt.items.map((item) => 
                            item.itemId === oldRow.itemId ? { ...item, ...newRow } as ReceiptItemDto : item
                          );
                          setReceipt(prev => ({ ...prev, items: updatedItems }));

                          // Validate the updated row
                          const rowIndex = receipt.items.findIndex((item) => item.itemId === newRow.itemId);
                          const error = validateReceiptItemRow(newRow as ReceiptItemDto);

                          // Update validation errors state
                          const newErrors = { ...itemValidationErrors };
                          if (error) {
                            newErrors[`items[${rowIndex}]`] = error;
                          } else {
                            delete newErrors[`items[${rowIndex}]`];
                          }
                          setItemValidationErrors(newErrors);

                          // Update form validation if there's an active form
                          if (formApiRef.current) {
                            formApiRef.current.change("_dataGridValidated", Object.keys(newErrors).length === 0);

                            // Get the current form values to update the state
                            // This will trigger the useEffect for change detection
                            if (formApiRef.current.getState().values) {
                              setCurrentFormValues({ ...formApiRef.current.getState().values });
                            }
                          }

                          return newRow;
                        }}
                        isCellEditable={() => !isReadonly}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ mt: 2 }}
                        onClick={handleAddRow}
                        disabled={isReadonly}
                      >
                        {t("common.addItem")}
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
                      <Button variant="contained" color="secondary" onClick={handleCancel}>
                        {t("common.cancel")}
                      </Button>
                      <Button variant="contained" color="primary" type="submit" disabled={isReadonly || submitting}>
                        {t("common.save")}
                      </Button>
                      {shouldShowActionButtons(receipt.status) && !isFinalState(receipt.status) && (
                        <>
                          <Button variant="contained" color="primary" disabled={isReadonly} onClick={handleReject}>
                            {t("receiptOverview.table.reject", "Reject")}
                          </Button>
                          <Button variant="contained" color="primary" disabled={isReadonly} onClick={handleApprove}>
                            {t("receiptOverview.table.approve", "Approve")}
                          </Button>
                        </>
                      )}
                    </Box>

                    {showSaveConfirmDialog && (
                      <Dialog onClose={() => setShowSaveConfirmDialog(false)} open={true}>
                        <DialogTitle>{t("receiptScanDialog.unsavedChangesTitle", "Unsaved Changes")}</DialogTitle>
                        <DialogContent>
                          <Box sx={{ mb: 2 }}>
                            {t(
                              "receiptScanDialog.unsavedChangesMessage",
                              "You have unsaved changes. What would you like to do?"
                            )}
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography color="textSecondary" variant="body2">
                              {t(
                                "receiptScanDialog.unsavedChangesHint",
                                "Click 'Save Changes' to save your changes before continuing, 'Discard Changes' to proceed without saving, or 'Cancel' to return to editing."
                              )}
                            </Typography>
                          </Box>

                          <Grid container justifyContent="flex-end" spacing={1}>
                            <Grid item>
                              <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => {
                                  // Simply close the dialog without doing anything
                                  setShowSaveConfirmDialog(false);
                                  setPendingAction(null);
                                }}
                              >
                                {t("common.cancel", "Cancel")}
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => {
                                  setShowSaveConfirmDialog(false);

                                  // Before submitting, make sure all edited cells in DataGrid are committed
                                  const gridApi = document
                                    .querySelector(".MuiDataGrid-root")
                                    ?.querySelector(".MuiDataGrid-virtualScroller");
                                  if (gridApi) {
                                    // Try to click somewhere else to commit any cell edits
                                    (gridApi as HTMLElement).click();
                                  }

                                  // Short delay to ensure cell edits are committed
                                  setTimeout(() => {
                                    // Validate before submitting
                                    if (validateAllReceiptItems()) {
                                      // Save the pending action before it gets cleared
                                      const currentAction = pendingAction;

                                      try {
                                        // Get the form values safely
                                        const formState = form.getState();
                                        if (!formState || !formState.values) {
                                          console.error("Form values not available");
                                          setPendingAction(null);
                                          return;
                                        }

                                        const formValues = formState.values as ReceiptDto;

                                        // Execute the update and chain the pending action
                                        const savePromise = updateReceipt(formValues);

                                        // Only proceed if updateReceipt returns a promise
                                        if (savePromise) {
                                          savePromise
                                            .then(() => {
                                              // After successful save and state update, execute the pending action
                                              if (currentAction === "approve") {
                                                approveReceipt();
                                              } else if (currentAction === "reject") {
                                                rejectReceipt();
                                              }
                                            })
                                            .catch(() => {
                                              // If save failed, don't proceed with the action
                                              console.error("Save failed, cannot proceed with the pending action");
                                            })
                                            .finally(() => {
                                              // Reset the pending action state
                                              setPendingAction(null);
                                            });
                                        } else {
                                          // If no promise was returned, reset the pending action
                                          setPendingAction(null);
                                        }
                                      } catch (error) {
                                        console.error("Error handling form submission:", error);
                                        setPendingAction(null);
                                      }
                                    } else {
                                      // Reset pending action if validation fails
                                      setPendingAction(null);
                                    }
                                  }, 100);
                                }}
                              >
                                {t("receiptScanDialog.saveChanges", "Save Changes")}
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  setShowSaveConfirmDialog(false);
                                  // Discard changes and proceed with action immediately
                                  if (pendingAction === "approve") {
                                    approveReceipt();
                                  } else if (pendingAction === "reject") {
                                    rejectReceipt();
                                  }
                                  setPendingAction(null);
                                }}
                              >
                                {t("receiptScanDialog.discardChanges", "Discard Changes")}
                              </Button>
                            </Grid>
                          </Grid>
                        </DialogContent>
                      </Dialog>
                    )}
                  </form>
                );
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </MenuLayout>
  );
};

const mapStateToProps = (state: IStore) => ({
  operationalUnits: state.operationalUnits.operationalUnits,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkCreateErrorNotification,
      thunkCreateSuccessNotification,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(ReceiptEdit);
